/**
 * The database enums, mirrored once for both sides.
 *
 * **Migration zero is the authority.** Every enum below has a `CREATE TYPE` in
 * `apps/api/prisma/migrations/00000000000000_init/migration.sql`, and its
 * members are that type's members, in that order. Nothing here is invented: a
 * value that exists only in TypeScript is a value the database will reject on
 * insert, and it will be rejected in production rather than in review.
 *
 * `UserRole` and `Permission` are the exception to "one enum, one CREATE TYPE" -
 * they are rows in `roles` and `permissions`, seeded by `apps/api/prisma/seed.ts`
 * from the matrix of `uml/01-rg-ro.md` section 8. The seed file is their
 * authority in the same way.
 *
 * Reconciled when branch `main` merged into `reda`. Both sides had drifted from
 * migration zero in opposite directions; the notes on each enum record what was
 * wrong, so the same value is not reintroduced from memory.
 */

// ============================================================
// ACCESS
// ============================================================

/**
 * The ten roles of the matrix in uml/01-rg-ro.md section 8, seeded by
 * apps/api/prisma/seed.ts.
 *
 * Corrected in FND-2.4: this list previously held a single DORMITORY_MANAGER
 * and a generic COMPLAINT_MANAGER. The matrix has the two dormitories as
 * separate roles and has no generic complaint role, and the database agrees -
 * complaint_category is BOYS_DORMITORY, GIRLS_DORMITORY, RESTAURANT,
 * CARPOOLING, CAMPUS, OTHER. CMP-1.1 `roleFor(category)` maps one to the other.
 */
export enum UserRole {
  STUDENT = 'STUDENT',
  DRIVER = 'DRIVER',
  BOYS_DORMITORY_MANAGER = 'BOYS_DORMITORY_MANAGER',
  GIRLS_DORMITORY_MANAGER = 'GIRLS_DORMITORY_MANAGER',
  ACADEMIC_MANAGER = 'ACADEMIC_MANAGER',
  CATERING_MANAGER = 'CATERING_MANAGER',
  EVENT_MANAGER = 'EVENT_MANAGER',
  ANNOUNCEMENT_MANAGER = 'ANNOUNCEMENT_MANAGER',
  CARPOOLING_MANAGER = 'CARPOOLING_MANAGER',
  /**
   * Not one of the ten roles of the matrix and **not seeded** by
   * apps/api/prisma/seed.ts, so `hasRole(user, ADMIN)` is false for every
   * account this API creates. It is kept because the announcements, complaints
   * and admin screens merged from `origin/omar` read it, and deleting it would
   * break their build rather than fix the data. Whoever reconciles ACCESS
   * (STATE open item 6) decides whether it becomes an eleventh seeded row or
   * those screens move to PRESIDENT.
   */
  ADMIN = 'ADMIN',
  PRESIDENT = 'PRESIDENT',
}

/**
 * RG10: members of these roles implicitly hold every permission. From omar's
 * 0b3ae71 - one list, so guards, services and the "last privileged user" check
 * never re-declare it. ADMIN is the web-side label of the note above; the
 * database role that actually carries RG10 is PRESIDENT.
 */
export const PRIVILEGED_ROLES: readonly UserRole[] = [UserRole.PRESIDENT, UserRole.ADMIN];

/**
 * `user_status`. Two values, not three.
 *
 * A PENDING_VERIFICATION member was proposed and does not exist in the
 * database: `users.status` defaults to ACTIVE at sign-up and an unverified
 * account is one with an unconsumed row in `verification_codes`
 * (`purpose = 'SIGNUP'`, `consumed_at IS NULL`). Verification is a row's
 * lifecycle, not a user's status - ACC-2 reads that table.
 *
 * RG145, RG146: ANONYMISED keeps the account's 26 relations intact.
 */
export enum UserStatus {
  ACTIVE = 'ACTIVE',
  ANONYMISED = 'ANONYMISED',
}

/** `verification_purpose`. ACC-2 sign-up, ACC-5 password reset. */
export enum VerificationPurpose {
  SIGNUP = 'SIGNUP',
  PASSWORD_RESET = 'PASSWORD_RESET',
}

/**
 * `suspension_scope`. One value.
 *
 * RG09 scopes suspension rather than making it global, and today carpooling is
 * the only module that suspends anyone. `AccessService.isSuspended` is typed
 * wider - see `SuspensionScope` in `apps/api/src/access/access.service.ts` -
 * because the contract anticipates LIBRARY and EVENTS; the database gets a new
 * value the day a rule actually needs one.
 */
export enum SuspensionScope {
  CARPOOLING = 'CARPOOLING',
}

/**
 * The 17 permission codes seeded by `apps/api/prisma/seed.ts`, taken from the
 * Permission column of every endpoint table in `uml/18-api-specification.md`.
 *
 * A DRIVER member was proposed and is not a permission. Proposing a ride is a
 * role check (`UserRole.DRIVER`), not a permission code - uml/18 section 2.3,
 * and seed.ts says so at the head of its PERMISSIONS list. Adding it here would
 * make `hasPermission(user, 'DRIVER')` look like a real call, and it would
 * return false forever because nothing seeds that row.
 *
 * STUDENT and DRIVER hold no permission code at all. That emptiness is the
 * design: a student endpoint that starts requiring a code is a design change.
 */
export enum Permission {
  ANNOUNCEMENT_PUBLISH = 'ANNOUNCEMENT_PUBLISH',
  CARPOOL_MANAGE = 'CARPOOL_MANAGE',
  CHECKIN_MANAGE = 'CHECKIN_MANAGE',
  COMPLAINT_FORWARD = 'COMPLAINT_FORWARD',
  COMPLAINT_HISTORY = 'COMPLAINT_HISTORY',
  EVENT_MANAGE = 'EVENT_MANAGE',
  LIBRARY_MANAGE = 'LIBRARY_MANAGE',
  LIBRARY_REVIEW = 'LIBRARY_REVIEW',
  MEAL_HEADCOUNT = 'MEAL_HEADCOUNT',
  MEAL_PHOTO_UPLOAD = 'MEAL_PHOTO_UPLOAD',
  MENU_EDIT = 'MENU_EDIT',
  MENU_MANAGE = 'MENU_MANAGE',
  MENU_PUBLISH = 'MENU_PUBLISH',
  ROLE_GRANT = 'ROLE_GRANT',
  USER_ANONYMISE = 'USER_ANONYMISE',
  USER_LIST = 'USER_LIST',
}

// ============================================================
// COMPLAINTS
// ============================================================

/** `complaint_category`. RG14 - a complaint targets exactly one. */
export enum ComplaintCategory {
  BOYS_DORMITORY = 'BOYS_DORMITORY',
  GIRLS_DORMITORY = 'GIRLS_DORMITORY',
  RESTAURANT = 'RESTAURANT',
  CARPOOLING = 'CARPOOLING',
  CAMPUS = 'CAMPUS',
  OTHER = 'OTHER',
}

/**
 * `complaint_status`. Three values.
 *
 * Two wrong versions met here and neither matched the database. One had
 * SUBMITTED / IN_PROGRESS / RESOLVED / ARCHIVED, which names no database value
 * at all. The other had OPEN / RESOLVED / CLOSED_WITHOUT_CONFIRMATION /
 * ARCHIVED, which is the right shape but calls CLOSED_RESOLVED "RESOLVED" and
 * adds an ARCHIVED the column cannot hold.
 *
 * The state diagram of `uml/12-state-diagrams.md` section 3 has five states and
 * the column has three, because two of them are derived and must stay derived:
 *
 * - **Stale** - RG139, seven days without a reply from the student. Computed
 *   from the last `complaint_messages` row, never written. A stale complaint is
 *   still OPEN, which is why the manager may still close it alone.
 * - **Archived** - RG20, "a closed complaint is archived, not deleted". Closure
 *   *is* archival; `closed_at IS NOT NULL` is the whole test. There is no
 *   archived column on `complaints` and adding a status member would invent a
 *   transition RG20 does not describe.
 *
 * CLOSED_RESOLVED and CLOSED_WITHOUT_CONFIRMATION stay distinct on purpose -
 * uml/12 section 3's note, RG22: an abandoned complaint is not the same result
 * as an agreed one, and managers assess their own work on the difference.
 */
export enum ComplaintStatus {
  OPEN = 'OPEN',
  CLOSED_RESOLVED = 'CLOSED_RESOLVED',
  CLOSED_WITHOUT_CONFIRMATION = 'CLOSED_WITHOUT_CONFIRMATION',
  /**
   * RESOLVED and AUTO_RESOLVED are **not** members of the `complaint_status`
   * type. Migration zero holds exactly the three above, so an insert of either
   * is rejected by Postgres.
   *
   * They are here because the complaint screens merged from `origin/omar` were
   * written against his own schema, where CLOSED_RESOLVED is split in two by
   * who closed it - the student (RESOLVED) or the seven-day sweep of RG139
   * (AUTO_RESOLVED). That distinction is real and the column does not record
   * it. Closing it means either a migration adding the two values or a derived
   * flag beside the status, and that is COMPLAINTS' decision, not this merge's.
   */
  RESOLVED = 'RESOLVED',
  AUTO_RESOLVED = 'AUTO_RESOLVED',
}

// ============================================================
// SHARED - notifications
// ============================================================

/** `notification_type`. What produced the notification. */
export enum NotificationType {
  ANNOUNCEMENT = 'ANNOUNCEMENT',
  MENU_CHANGE = 'MENU_CHANGE',
  PROPOSAL_DECISION = 'PROPOSAL_DECISION',
}

/** `announcements.category`. Read by the announcement screens. */
export enum AnnouncementCategory {
  ACADEMIC = 'ACADEMIC',
  RESIDENCE = 'RESIDENCE',
  RESTAURANT = 'RESTAURANT',
  EVENT = 'EVENT',
  OTHER = 'OTHER',
}

/**
 * No column holds this. See `NotificationStatus` below for why the channel was
 * dropped from the schema; it survives here only because the notification
 * preference screen merged from `origin/omar` reads its members.
 */
export enum NotificationChannel {
  EMAIL = 'EMAIL',
  IN_APP = 'IN_APP',
}

/**
 * `notification_status`.
 *
 * A NotificationChannel enum (EMAIL / IN_APP) was proposed and is dropped: the
 * `notifications` table has `type`, `subject`, `status` and `sent_at`, and no
 * channel column. Every notification is a row plus an e-mail through
 * `MailGateway`; there is nothing to select between. SHARED adds the enum with
 * the migration that adds the column, if RG26's opt-in ever needs one.
 */
export enum NotificationStatus {
  PENDING = 'PENDING',
  SENT = 'SENT',
  FAILED = 'FAILED',
}

// ============================================================
// EVENTS
// ============================================================

/**
 * `event_status`. The middle value is SCHEDULED, not PUBLISHED.
 *
 * RG30: an event without a known date is COMING_SOON and is not in the
 * calendar. RG31: it "enters the calendar" when the manager sets the date -
 * SCHEDULED is that state, and the column names it. RG32 archives on expiry;
 * RG140 keeps COMING_SOON out of that sweep, since without a real date the
 * expiry date means nothing.
 *
 * `uml/12-state-diagrams.md` section 4 draws this node as `Published`. The
 * diagram's label and the column's value are the same state under two names and
 * the column wins in code. EVENTS owns the doc fix.
 */
export enum EventStatus {
  COMING_SOON = 'COMING_SOON',
  SCHEDULED = 'SCHEDULED',
  /**
   * An alias of SCHEDULED, not a fourth state - the events screens merged from
   * `origin/oubay_branch` spell it the way uml/12 section 4 draws it. Same
   * string, so a comparison against either member works and neither can reach
   * the database as a value the column rejects. New code names SCHEDULED.
   */
  PUBLISHED = 'SCHEDULED',
  ARCHIVED = 'ARCHIVED',
}

export enum Gender {
  MALE = 'MALE',
  FEMALE = 'FEMALE',
  UNSPECIFIED = 'UNSPECIFIED',
}

// ============================================================
// CARPOOLING
// ============================================================

/** `ride_status`. RG40, RG102, RG44, RG141. */
export enum RideStatus {
  OPEN = 'OPEN',
  FULL = 'FULL',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

/** `booking_status`. A cancellation keeps the row - RG105. */
export enum BookingStatus {
  PENDING = 'PENDING',
  CONFIRMED = 'CONFIRMED',
  REJECTED = 'REJECTED',
  CANCELLED = 'CANCELLED',
}

// `BookingStatus` and `SuspensionScope` were declared a second time here by the
// carpooling merge, identically to the versions above. Duplicates, not a second
// meaning - removed.

export enum IncidentCategory {
  DANGEROUS_DRIVING = 'DANGEROUS_DRIVING',
  LADIES_ONLY_BREACH = 'LADIES_ONLY_BREACH',
  UNJUSTIFIED_REMOVAL = 'UNJUSTIFIED_REMOVAL',
  NO_SHOW = 'NO_SHOW',
  HARASSMENT = 'HARASSMENT',
  OTHER = 'OTHER',
}

export enum IncidentStatus {
  OPEN = 'OPEN',
  INVESTIGATING = 'INVESTIGATING',
  RESOLVED = 'RESOLVED',
  DISMISSED = 'DISMISSED',
}

// ============================================================
// RESTAURANT
// ============================================================

/**
 * The three RESTAURANT enums are string unions with a `const` array beside
 * them, and NOT TypeScript `enum`s like the ACCESS ones above.
 *
 * Prisma generates a const object plus a union of the literal strings:
 *
 *     export type weekly_menu_status = 'DRAFT' | 'PUBLISHED' | ...
 *
 * A string `enum` member is nominal, so that union does not assign to it -
 * `Type '"DRAFT"' is not assignable to type 'WeeklyMenuStatus'`. A repository
 * handing a Prisma row straight into a response DTO would not compile, and the
 * usual escape is a cast, which throws the checking away entirely. The union
 * assigns in both directions.
 *
 * The `as const` array is also what `z.enum()` needs: it takes a tuple of
 * literals, not a TypeScript enum.
 *
 * The ACCESS and COMPLAINTS enums above are still `enum`s because the web app
 * reads their members (`UserRole.PRESIDENT`) and nothing has yet handed a
 * Prisma value into one. The first repository that does will hit exactly this,
 * and the fix is this shape.
 */

/**
 * `weekly_menu_status`. Named for its type, not shortened to MenuStatus - there
 * is no `menus` table and the shorter name reads as one.
 *
 * DRAFT -> PUBLISHED (RG56) or DRAFT -> NO_SERVICE (RG144); both reach ARCHIVED
 * through the phase 5 sweep. `uml/12-state-diagrams.md` section 6.
 */
export const WEEKLY_MENU_STATUSES = ['DRAFT', 'PUBLISHED', 'ARCHIVED', 'NO_SERVICE'] as const;
export type WeeklyMenuStatus = (typeof WEEKLY_MENU_STATUSES)[number];

/** `meal_type`. RG50 - each day carries these three. */
export const MEAL_TYPES = ['BREAKFAST', 'LUNCH', 'DINNER'] as const;
export type MealType = (typeof MEAL_TYPES)[number];

/**
 * `day_of_week`. Seven values, Monday first.
 *
 * RG50 and D-006: a weekly menu is seven days starting Monday, and this array
 * is declared in that order.
 *
 * Do not index it with `Date.prototype.getUTCDay()`, which is Sunday-first:
 * every day would be named wrong by one. `restaurant/domain/week.ts` keeps its
 * own Sunday-first lookup for exactly that reason, and the two orders are the
 * hazard rather than an oversight.
 */
export const DAYS_OF_WEEK = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
] as const;
export type DayOfWeek = (typeof DAYS_OF_WEEK)[number];

// ============================================================
// LIBRARY
// ============================================================

/** `document_status`. RG89, RG90 - the Academic Manager decides. */
export enum DocumentStatus {
  PENDING = 'PENDING',
  PUBLISHED = 'PUBLISHED',
  REJECTED = 'REJECTED',
}

/** `review_decision`. The decision recorded on a proposal review. */
export enum ReviewDecision {
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

/** `report_status`. RG117 - a reported document is triaged, not deleted. */
export enum ReportStatus {
  PENDING = 'PENDING',
  DISMISSED = 'DISMISSED',
  RESOLVED = 'RESOLVED',
}
